/**
 * The two boards: most recently dead, and longest-surviving.
 *
 * The framing is the product's, not a scoreboard's. Everybody on this page is dead — that is
 * the long-standing filter, and `jobs/leaderboard.py` restates why keeping it matters: a living run on
 * "longest surviving" would rank above finished ones, show no cause of death, and move while
 * its player is still playing. So the columns are *how long they lasted* and *what got them*,
 * and the second one is the one people read. The cause of death is the DM's sarcastic
 * one-liner, and a leaderboard of those is the best advertisement this game has.
 *
 * ## Three languages on one board (L4)
 *
 * `api/leaderboards.json` is **one global file** and this page renders it in the *reader's*
 * locale, but every epitaph on it is in the language of the story that earned it. So a German
 * reader meets Ukrainian and English prose under German headings, and the only question was
 * what to do about it. The epitaph ships as the DM wrote it and the row says which language
 * that is: filtering the board to the reader would show a small player base an empty page, and
 * translating the sentence would spend a model call to destroy the one thing on it worth
 * reading. `jobs/leaderboard.py` carries the same argument on the writing side.
 *
 * The tag is only drawn on a *foreign* line — telling a German reader that a German epitaph is
 * in German is noise. `lang` is set on every epitaph regardless, because that one is not for
 * the reader's eyes: it is what stops a screen reader pronouncing Ukrainian with English
 * phonemes, the same bug L10.1 fixed for the document.
 */
import type { LeaderboardEntry, LeaderboardFile } from '@/features/leaderboard/board';
import { localeLabel } from '@/features/settings/locales';
import { useT } from '@/i18n/context';
import type { UiKey } from '@/i18n/en.generated';
import { intlLocale } from '@/i18n/t';
import { relativeTime } from '@/ui/time';

export function Leaderboard({ file }: { file: LeaderboardFile }) {
  const t = useT();

  return (
    <div className="flex flex-col gap-10">
      <Board
        title="ui.leaderboard.recent"
        entries={file.recent}
        empty="ui.leaderboard.recent_empty"
      />
      <Board
        title="ui.leaderboard.longest"
        entries={file.longest}
        empty="ui.leaderboard.longest_empty"
      />
      <p className="text-fg-faint font-mono" style={{ fontSize: 'var(--text-meta)' }}>
        {t('ui.leaderboard.updated', { when: relativeTime(t, file.generated_at) })}
      </p>
    </div>
  );
}

function Board({
  title,
  entries,
  empty,
}: {
  title: UiKey;
  entries: readonly LeaderboardEntry[];
  empty: UiKey;
}) {
  const t = useT();

  return (
    <section>
      <h2
        className="text-fg-muted mb-3 font-mono tracking-wider"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {t(title)}
      </h2>

      {entries.length === 0 ? (
        <p className="text-fg-faint" style={{ fontSize: 'var(--text-meta)' }}>
          {t(empty)}
        </p>
      ) : (
        <ol className="flex flex-col">
          {entries.map((entry, index) => (
            <li
              key={`${entry.character_name}-${String(index)}`}
              className="border-rule flex gap-4 border-t py-3 last:border-b"
            >
              <span
                className="text-fg-faint shrink-0 font-mono tabular-nums"
                style={{ fontSize: 'var(--text-meta)' }}
              >
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="min-w-0">
                <p className="text-fg font-mono" style={{ fontSize: 'var(--text-action)' }}>
                  {entry.character_name}
                  <span className="text-fg-faint">
                    {' · '}
                    {t.plural('ui.story.chapters', entry.chapter_count)}
                  </span>
                </p>
                {/*
                 * The epitaph is set in the narration serif: it is the DM's sentence, written
                 * in the world's voice, and it is the only prose on this page. Everything
                 * around it is chrome and stays mono (`tokens.css`, "three faces, three jobs").
                 */}
                {entry.cause_of_death ? (
                  <p
                    lang={intlLocale(entry.locale)}
                    className="text-fg-muted mt-1"
                    style={{
                      fontFamily: 'var(--font-narration)',
                      fontSize: 'var(--text-action)',
                      lineHeight: 1.5,
                    }}
                  >
                    {entry.cause_of_death}
                    {entry.locale === t.locale ? null : (
                      /*
                       * Inside the epitaph's own paragraph so it wraps as part of the
                       * sentence rather than claiming a line of its own — but with its own
                       * `lang`, because the tag is written in the *reader's* language even
                       * though the name inside it is an endonym (`settings/locales.ts`).
                       */
                      <span
                        lang={intlLocale(t.locale)}
                        className="text-fg-faint ml-2 font-mono whitespace-nowrap"
                        style={{ fontSize: 'var(--text-meta)' }}
                      >
                        {t('ui.leaderboard.in_language', {
                          language: localeLabel(entry.locale),
                        })}
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-fg-faint mt-1" style={{ fontSize: 'var(--text-meta)' }}>
                    {t('ui.leaderboard.still_out_there')}
                  </p>
                )}
                {entry.completed_at ? (
                  <p
                    className="text-fg-faint mt-1 font-mono"
                    style={{ fontSize: 'var(--text-meta)' }}
                  >
                    {relativeTime(t, entry.completed_at)}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
